import { useCallback, useEffect, useRef, useState } from 'react';

/** Per-user column widths for the compact list headers (Live Map). Dragging the
 *  handle at a header cell's inline-end edge resizes it; double-click resets. */
export const MIN_COLUMN_WIDTH = 28;

/** Pure: the width a drag lands on. In RTL the handle is on the LEFT edge of the
 *  cell, so moving the pointer left makes the column wider. */
export function nextColumnWidth({ startWidth, startX, clientX, rtl, min = MIN_COLUMN_WIDTH }) {
  const delta = (clientX - startX) * (rtl ? -1 : 1);
  return Math.max(min, Math.round(startWidth + delta));
}

function readStored(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

export function useColumnWidths(storageKey) {
  const [widths, setWidths] = useState(() => readStored(storageKey));
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch { /* private mode */ }
  }, [storageKey, widths]);

  const widthOf = useCallback((colId, fallback) => {
    const w = widths[colId];
    return typeof w === 'number' && Number.isFinite(w) ? w : fallback;
  }, [widths]);
  const setWidth = useCallback((colId, w) => setWidths((prev) => ({ ...prev, [colId]: w })), []);
  const resetWidth = useCallback((colId) => setWidths((prev) => { const { [colId]: _drop, ...rest } = prev; return rest; }), []);

  // Live drag: listeners on document so the pointer may leave the handle.
  const dragRef = useRef(null);
  useEffect(() => () => { if (dragRef.current) dragRef.current.cleanup(); }, []);
  const startResize = useCallback((colId, e, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const startX = e.clientX;
    const onMove = (ev) => setWidth(colId, nextColumnWidth({ startWidth: currentWidth, startX, clientX: ev.clientX, rtl }));
    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanup);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanup);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    dragRef.current = { cleanup };
  }, [setWidth]);

  return { widths, widthOf, setWidth, resetWidth, startResize };
}
